"use client";

import { ReactNode, useEffect, useRef } from "react";
import { useSpotlight } from "./SpotlightContext";

interface SpotlightAnchorProps {
  children: ReactNode;
  step: number;
  margin?: number;
  rounded?: string;
  popover?: ReactNode;
  className?: string;
  gradientWidth?: number;
  gradientHeight?: number;
}

export function SpotlightAnchor({
  children,
  step,
  margin = 20,
  rounded = "2xl",
  popover,
  className = "",
  gradientWidth,
  gradientHeight,
}: SpotlightAnchorProps) {
  const { activeStep, registerTarget, unregisterTarget } = useSpotlight();
  const containerRef = useRef<HTMLDivElement>(null);
  const isActive = activeStep === step;

  useEffect(() => {
    if (containerRef.current) {
      const update = () => {
        if (containerRef.current) {
          registerTarget(step, {
            rect: containerRef.current.getBoundingClientRect(),
            margin,
            rounded,
            popover: activeStep === step ? popover : undefined,
            gradientWidth,
            gradientHeight,
          });
        }
      };

      update();

      // Use ResizeObserver for more reliable updates
      const resizeObserver = new ResizeObserver(() => {
        update();
      });

      resizeObserver.observe(containerRef.current);
      window.addEventListener("resize", update);
      window.addEventListener("scroll", update, true);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("resize", update);
        window.removeEventListener("scroll", update, true);
        unregisterTarget(step);
      };
    }
  }, [
    step,
    margin,
    rounded,
    registerTarget,
    unregisterTarget,
    activeStep,
    gradientWidth,
    gradientHeight,
  ]);

  // Force update position when this step becomes active
  useEffect(() => {
    if (isActive && containerRef.current) {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        if (containerRef.current) {
          registerTarget(step, {
            rect: containerRef.current.getBoundingClientRect(),
            margin,
            rounded,
            popover,
            gradientWidth,
            gradientHeight,
          });
        }
      });
    }
  }, [
    isActive,
    step,
    margin,
    rounded,
    registerTarget,
    popover,
    gradientWidth,
    gradientHeight,
  ]);

  return (
    <div
      ref={containerRef}
      className={`relative ${isActive ? "z-[9999]" : ""} ${className}`}
    >
      {children}
    </div>
  );
}
